import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BaseComponent } from '../../../common/components/base.component';
import { AlertifyService } from '../../../services/alertify.service';
import { HubApiService } from '../../../services/hub.api.service';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ImageService } from '../../../services/image.service';
import { HubComponent } from '../hub.component';
import { ModalService } from '../../../common/services/modal.service';
import { ButtonTriggerEvent } from '../../../common/components';
import { ConfirmModalActions, ConfirmModalData } from '../../../common/components/confirm.modal/confirm.modal.provider';
import { ConfirmModalComponent } from '../../../common/components/confirm.modal/confirm.modal.component';
import { TravelBuddySettings } from '../../../models/hub/travel.buddy.settings';
import { isEmptyArray } from '../../../common/extensions';
import {
  ImageSelectorCommand,
  ImageSelectorComponent,
} from '../../../common/components/image.selector/image.selector.component';

@Component({
  selector: 'ig-ai-settings',
  templateUrl: './ai.settings.component.html',
  styleUrls: ['./ai.settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AISettingsComponent extends BaseComponent implements OnInit {
  public aiSettings: {
    travelBuddy: TravelBuddySettings;
  };
  public selectedFiles: File[] = [];
  public failedLinksArr: any = [];
  public clientUploadedFiles: {
    name: string;
    openAIFileId: string;
    size: number;
    link?: string;
    status?: string;
    lastError?: any;
    uploadedAt: string;
  }[] = [];

  public hubName: string;
  public form: FormGroup;
  public linkContent: { oriLink; content; links; checked; status: 'initial' | 'uploading' | 'uploaded' | 'error' }[] =
    [];
  public isIncludeLinks = false;
  public isExtracting = false;
  public isUploadLinkContentEnded = false;
  public uploadLimit = 100;

  public isLoading = true;
  public isLoadingClinetUploadedFiles = false;

  panelOpenState = false;
  logoImgData: string = '';
  command: ImageSelectorCommand = new ImageSelectorCommand();

  @ViewChild('imgSelector') public logoImgSelector: ImageSelectorComponent;

  constructor(
    private hubApiService: HubApiService,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private parent: HubComponent,
    private imageService: ImageService,
    private alertify: AlertifyService,
    private modalService: ModalService
  ) {
    super({ activatedRoute });
  }

  public ngOnInit() {
    let { hubName } = this.allRouteParams;
    this.hubName = hubName;
    this.form = this.formBuilder.group({
      link: [null, [Validators.required]],
      linkContent: [null, []],
      defaultQuestion1: [null, []],
      defaultQuestion2: [null, []],
      introTitle: [null, []],
      introImage: [null, []],
      introContent: [null, []],
      avatarName: [null, []],
      avatarProfile: [null, []],
      avatarThumbnail: [null, []],
      avatarIntro: [null, []],
      logo: [null, []],
      privacyPolicyLink: [null, []],
      prompt: [null, []],
    });

    this.getAISettings();
    this.getTravelBuddyClientUploadedFiles();
  }

  public getAISettings() {
    this.hubApiService.getAISettings(this.hubName).subscribe((data) => {
      if (!data) {
        this.alertify.error(`Something went wrong`);
        return;
      }

      if (data.travelBuddy.ui.introduction && isEmptyArray(data.travelBuddy.ui.questions)) {
        data.travelBuddy.ui.questions = ['', ''];
      }
      if (!data.travelBuddy.ui.avatar.intro) {
        data.travelBuddy.ui.avatar.intro = `I'm your personal AI-powered travel assistant. Simply ask me a question, and I will provide you with relevant travel information and suggestions for your next trip to ${data.travelBuddy.cityName}!`;
      }
      this.aiSettings = data;
      this.isLoading = false;
    });
  }

  public getTravelBuddyClientUploadedFiles() {
    this.isLoadingClinetUploadedFiles = true;
    this.hubApiService.getTravelBuddyClientUploadedFiles(this.hubName).subscribe((data) => {
      this.clientUploadedFiles = data;
      this.isLoadingClinetUploadedFiles = false;
    });
  }

  public uploadTravelBuddyClientFiles(event: ButtonTriggerEvent) {
    if (this.clientUploadedFiles.length + this.selectedFiles.length > this.uploadLimit) {
      this.alertify.warning(`You can only upload a maximum of ${this.uploadLimit} files/links.`);
      event.immediateComplete();
      return;
    }

    this.hubApiService
      .uploadTravelBuddyClientFiles(this.hubName, this.selectedFiles)
      .finally(event.immediateComplete)
      .subscribe((data) => {
        this.selectedFiles = [];
        this.getTravelBuddyClientUploadedFiles();
      });
  }

  fileRead($event: any): void {
    this.selectedFiles = Array.from($event);
  }

  deleteFile(file) {
    let actions = <ConfirmModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
        this.hubApiService
          .deleteTravelBuddyClientFile(this.hubName, file.openAIFileId)
          .finally(event.complete)
          .subscribe((data) => {
            this.getTravelBuddyClientUploadedFiles();
          });
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription: 'Are you sure you want to delete this file permanently?',
      confirmModalConfirmButtonText: 'Delete',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  async extractLink(event: ButtonTriggerEvent) {
    let link = this.form.get('link').value;

    if (link) {
      this.isExtracting = true;
      this.linkContent = [];
      this.failedLinksArr = [];
      const data = await this.hubApiService.extractContentFromLink(this.hubName, link);

      if (!data.content) {
        this.isExtracting = false;
        event.immediateComplete();
        this.alertify.warning('No content found');
        return;
      }
      if (data.failedLinks.length > 0)
        this.alertify.warning('Some links failed to extract content : ', data.failedLinks);

      this.linkContent = [
        {
          oriLink: link,
          content: data.content,
          links: data.links,
          checked: true,
          status: 'initial',
        },
      ];

      if (this.isIncludeLinks && data.links.length) {
        // const links = data.links.slice(0, 5);
        for (const l of data.links) {
          const dataInSub = await this.hubApiService.extractContentFromLink(this.hubName, l);
          if (dataInSub.failedLinks.length > 0)
            this.failedLinksArr = [...this.failedLinksArr, ...dataInSub.failedLinks];
          if (this.linkContent.any((x) => x.oriLink === dataInSub.oriLink) || !dataInSub.content) continue;
          this.linkContent = [
            ...this.linkContent,
            {
              oriLink: dataInSub.oriLink,
              content: dataInSub.content,
              links: dataInSub.links,
              checked: true,
              status: 'initial',
            },
          ];
          console.log('this.linkContent', this.linkContent);
        }
      }

      // if (this.failedLinksArr.length > 0) {
      //   this.alertify.warning('Some links failed to extract content:\n' + this.failedLinksArr.join('\n'));
      // }

      this.isExtracting = false;
      this.isUploadLinkContentEnded = false;
      event.immediateComplete();
    } else {
      event.immediateComplete();
    }
  }

  async uploadExtractedContent(event: ButtonTriggerEvent) {
    if (this.clientUploadedFiles.length + this.linkContent.length > this.uploadLimit) {
      this.alertify.warning(`You can only upload a maximum of ${this.uploadLimit} files/links.`);
      event.immediateComplete();
      return;
    }

    for (const lc of this.linkContent.filter((x) => x.checked && x.content && !this.linkExists(x.oriLink))) {
      lc.status = 'uploading';
      try {
        await this.hubApiService.uploadLinkContent(this.hubName, lc.oriLink, lc.content);
        lc.status = 'uploaded';
      } catch (error) {
        console.error(error);
        lc.status = 'error';
      }
    }
    event.immediateComplete();
    this.isUploadLinkContentEnded = true;
    this.getTravelBuddyClientUploadedFiles();
  }

  linkExists(link: string) {
    return this.clientUploadedFiles.any((x) => x.link === link);
  }

  getIsChecked() {
    return this.linkContent.filter((x) => !this.linkExists(x.oriLink)).any((x) => x.checked);
  }

  toLocalTime(iso: string) {
    return new Date(iso).toLocaleString();
  }

  async saveUI(event: ButtonTriggerEvent) {
    if (this.aiSettings.travelBuddy.ui.otherInfo && this.aiSettings.travelBuddy.ui.otherInfo.privacyPolicyLink) {
      if (!this.aiSettings.travelBuddy.ui.otherInfo.privacyPolicyLink.startsWith('http')) {
        this.aiSettings.travelBuddy.ui.otherInfo.privacyPolicyLink =
          'https://' + this.aiSettings.travelBuddy.ui.otherInfo.privacyPolicyLink;
      }
    }

    if (this.logoImgData) {
      const res = await this.hubApiService.uploadBase64ImageToAWS(this.logoImgData, 300);
      if (res.success) {
        this.aiSettings.travelBuddy.ui.logo = res.url;
      } else {
        this.alertify.error('Upload logo image error, please try again.');
        return;
      }
    }

    this.hubApiService
      .setTravelBuddyUI(this.hubName, this.aiSettings.travelBuddy.ui)
      .finally(event.complete)
      .subscribe((data) => {
        this.alertify.successfully('Saved');
      });
  }

  chooseColor($event) {}

  get isImg() {
    // return url.match(/\.(jpeg|jpg|gif|png|webp)/) != null;
    if (this.logoImgData) {
      return true;
    }

    return this.aiSettings.travelBuddy.ui.logo && this.aiSettings.travelBuddy.ui.logo.startsWith('http');
  }

  get hasIntroduction() {
    if (!this.aiSettings || !this.aiSettings.travelBuddy || !this.aiSettings.travelBuddy.ui.introduction) {
      return false;
    }

    // check if any of the properties of tis.introduction are not null or empty
    for (const key in this.aiSettings.travelBuddy.ui.introduction) {
      if (this.aiSettings.travelBuddy.ui.introduction[key]) {
        return true;
      }
    }
    return false;
  }

  logoTextKeyup($event) {
    if ($event) {
      this.logoImgSelector.clearImage();
      this.logoImgData = '';
    }
  }

  promptTextKeyup($event) {
    if ($event) {
      this.logoImgSelector.clearImage();
      this.logoImgData = '';
    }
  }

  async setIndicator(event: ButtonTriggerEvent) {
    this.aiSettings.travelBuddy.indicator.prompt = this.form.get('prompt').value;
    this.hubApiService
      .setTravelBuddyIndicator(this.hubName, this.aiSettings.travelBuddy.indicator)
      .finally(event.complete)
      .subscribe((data) => {
        this.alertify.successfully('Saved');
      });
  }

  async onImageChange(file) {
    if (file) {
      this.logoImgData = await this.imageService.compressImageFileToBase64(file, 300);
      console.log(this.logoImgData);
      this.aiSettings.travelBuddy.ui.logo = '';
    }
  }

  getEmbedScript() {
    return `
      <div id="imgoingcalendar-wrapper" route="/${this.hubName}/places" url-change="true" version="V3" travel-buddy="true" travel-buddy-right="48px" travel-buddy-bottom="32px">
        <app-root></app-root>
        <script type="text/javascript" src="https://imgoingcalendar.com/imgoingscript.js"></script>
      </div>
    `;
  }

  getOnlyTravelBuddyEmbedScript() {
    return `
      <div id="imgoingcalendar-wrapper" route="/${this.hubName}/ai/travel-buddy" url-change="true" version="V3" travel-buddy-right="48px" travel-buddy-bottom="32px">
        <app-root></app-root>
        <script type="text/javascript" src="https://imgoingcalendar.com/imgoingscript.js"></script>
      </div>
    `;
  }

  copyLink() {
    this.copyToClip(this.getEmbedScript());
  }

  copyOnlyTravelBuddyLink() {
    this.copyToClip(this.getOnlyTravelBuddyEmbedScript());
  }

  private copyToClip(content) {
    let aux = document.createElement('input');
    aux.setAttribute('value', content);
    document.body.appendChild(aux);
    aux.select();
    document.execCommand('copy');
    document.body.removeChild(aux);
    this.alertify.success('Link is copied to the clipboard');
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/ai.settings/ai.settings.component.ts